import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';

const COLLAPSE_THRESHOLD = 20;

function buildInitialExpandedPaths(data) {
  const set = new Set();
  if (data === null || typeof data !== 'object') return set;
  function walk(val, path) {
    if (val === null || typeof val !== 'object') return;
    const isArr = Array.isArray(val);
    const n = isArr ? val.length : Object.keys(val).length;
    if (path === '' || n <= COLLAPSE_THRESHOLD) set.add(path);
    if (path === '' || n <= COLLAPSE_THRESHOLD) {
      const entries = isArr ? val.map((v, i) => [String(i), v]) : Object.entries(val);
      entries.forEach(([k, v]) => walk(v, path ? `${path}.${k}` : k));
    }
  }
  walk(data, '');
  return set;
}

function buildVisibleSnapshot(data, expandedPaths) {
  if (data === null || typeof data !== 'object') return data;
  const path = '';
  if (!expandedPaths.has(path)) {
    const isArr = Array.isArray(data);
    const n = isArr ? data.length : Object.keys(data).length;
    return isArr ? `[Array (${n} items)]` : `[Object (${n} keys)]`;
  }
  function walk(val, p) {
    if (val === null || typeof val !== 'object') return val;
    const arr = Array.isArray(val);
    const n = arr ? val.length : Object.keys(val).length;
    if (!expandedPaths.has(p)) return arr ? `[Array (${n} items)]` : `[Object (${n} keys)]`;
    if (arr) return val.map((v, i) => walk(v, `${p}.${i}`));
    const out = {};
    Object.entries(val).forEach(([k, v]) => { out[k] = walk(v, p ? `${p}.${k}` : k); });
    return out;
  }
  return walk(data, '');
}

function PrimitiveValue({ value }) {
  if (value === null) return <span className="json-viewer-null">null</span>;
  if (typeof value === 'boolean') return <span className="json-viewer-boolean">{String(value)}</span>;
  if (typeof value === 'number') return <span className="json-viewer-number">{value}</span>;
  if (typeof value === 'string') return <span className="json-viewer-string">&quot;{value}&quot;</span>;
  return <span className="json-viewer-other">{String(value)}</span>;
}

// Internal node that receives expandedPaths set and setExpandedPaths
function JsonNodeConnected({ value, label, path, expandedPaths, setExpandedPaths }) {
  const isObject = value !== null && typeof value === 'object' && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const isExpandable = isObject || isArray;
  const isExpanded = expandedPaths.has(path);

  const toggle = useCallback(() => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, [path, setExpandedPaths]);

  const count = isArray ? value.length : (isObject ? Object.keys(value).length : 0);
  const summary = isArray ? `Array (${count})` : `Object (${count})`;

  if (!isExpandable) {
    return (
      <div className="json-viewer-leaf">
        {label != null && <span className="json-viewer-key">{label}: </span>}
        <PrimitiveValue value={value} />
      </div>
    );
  }

  const entries = isArray ? value.map((v, i) => [String(i), v]) : Object.entries(value);

  return (
    <div className="json-viewer-node">
      <div
        className="json-viewer-toggle-row"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        aria-expanded={isExpanded}
      >
        <span className="json-viewer-toggle" aria-hidden>
          <i className={`fa-solid fa-chevron-right json-viewer-chevron ${isExpanded ? 'json-viewer-chevron-open' : ''}`} />
        </span>
        {label != null && <span className="json-viewer-key">{label}: </span>}
        <span className="json-viewer-summary">{summary}</span>
      </div>
      {isExpanded && (
        <div className="json-viewer-children">
          {entries.map(([k, v]) => (
            <div key={k} className="json-viewer-child">
              <JsonNodeConnected
                value={v}
                label={k}
                path={path ? `${path}.${k}` : k}
                expandedPaths={expandedPaths}
                setExpandedPaths={setExpandedPaths}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Interactive JSON viewer. Expandable nodes; objects/arrays with more than 20 items
 * start collapsed. Exposes getVisibleSnapshot() via ref for copy-as-JSON.
 */
const JsonViewer = forwardRef(function JsonViewer({ data }, ref) {
  const [expandedPaths, setExpandedPaths] = useState(() => new Set());

  useEffect(() => {
    if (data == null || typeof data !== 'object') return;
    setExpandedPaths(buildInitialExpandedPaths(data));
  }, [data]);

  const getVisibleSnapshot = useCallback(() => {
    if (data === undefined || data === null) return null;
    return buildVisibleSnapshot(data, expandedPaths);
  }, [data, expandedPaths]);

  useImperativeHandle(ref, () => ({ getVisibleSnapshot }), [getVisibleSnapshot]);

  if (data === undefined || data === null) {
    return <div className="json-viewer json-viewer-empty">No data</div>;
  }
  return (
    <div className="json-viewer">
      <JsonNodeConnected
        value={data}
        path=""
        expandedPaths={expandedPaths}
        setExpandedPaths={setExpandedPaths}
      />
    </div>
  );
});

export default JsonViewer;
